import { isPathActive, subItemPath } from '@/authz/menu-visibility';
import type { IHelpModuleSync } from '@/hooks/queries/help-centre/help-centre.types';
import type { IMenuItem } from '@/layout/utils';

/**
 * The Help Centre's module list is DERIVED from `menuData`, the same way route policies are.
 *
 * The server hides a guide whose module the reader cannot open, and it learns each module's gate
 * from this list (posted by the editor's "Sync modules from menu" button). So the gate a guide is
 * filtered by is, byte for byte, the gate the sidebar uses — including the borrowed ones (Support
 * Desk → `manage_agents`). Mirroring the *intended* gate instead would make the docs and the
 * sidebar disagree about who can see what. `help-modules.test.ts` pins the parity.
 */

const ADMIN_PREFIX = '/admin/';

const slug = (text: string) =>
	text
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

/** `/admin/elections/manage-agents` → `elections.manage-agents`. Safe in a URL segment unencoded. */
export const moduleKeyForUrl = (pageUrl: string): string =>
	pageUrl.replace(ADMIN_PREFIX, '').split('/').filter(Boolean).map(slug).join('.');

/** A sub-item's key hangs off its parent's; `url: ''` subs (VESR Dashboard, All Elections) use their title. */
export const subModuleKey = (parentKey: string, sub: { url: string; title: string }) =>
	`${parentKey}.${sub.url ? sub.url.split('/').map(slug).join('.') : slug(sub.title)}`;

export const buildHelpModules = (menu: readonly IMenuItem[]): IHelpModuleSync[] => {
	const modules: IHelpModuleSync[] = [];
	menu.forEach((item) => {
		const key = moduleKeyForUrl(item.pageUrl);
		modules.push({
			key,
			title: item.title,
			parentKey: null,
			journeyKey: journeyFor(key, item.section),
			routeUrl: item.pageUrl,
			requiredActions: item.roleActions.map((a) => a.toLowerCase()),
			superAdminOnly: !!item.superAdminOnly,
			observerAdminOnly: !!item.observerAdminOnly,
			// Every menu module is gated; "general" (ungated) modules are a server-side concept for P2.
			isGeneral: false,
			sortOrder: modules.length,
		});

		item.sub?.forEach((sub) => {
			const subKey = subModuleKey(key, sub);
			modules.push({
				key: subKey,
				title: sub.title,
				parentKey: key,
				journeyKey: journeyFor(subKey, item.section),
				routeUrl: subItemPath(item, sub),
				requiredActions: sub.roleActions.map((a) => a.toLowerCase()),
				superAdminOnly: !!sub.superAdminOnly,
				// Sub-items carry no observer flag; they inherit the parent's rail visibility.
				observerAdminOnly: !!item.observerAdminOnly,
				isGeneral: false,
				sortOrder: modules.length,
			});
		});
	});

	return modules;
};

/**
 * Modules that belong to no menu entry and are open to every signed-in user (`isGeneral: true`).
 *
 * The server has no bypass: a module with empty `requiredActions` and `isGeneral: false` is visible
 * to NOBODY, Super Admin included. Menu modules can never hit that (`ActionSet` is non-empty by type),
 * but signing in, the election picker and the glossary have no menu gate, so they live here.
 */
export const GENERAL_HELP_MODULES: IHelpModuleSync[] = [
	{ key: 'help.getting-started', title: 'Getting started', journeyKey: 'getting-started' },
	{ key: 'help.glossary', title: 'Glossary', journeyKey: 'getting-started' },
].map((m, i) => ({
	...m,
	parentKey: null,
	routeUrl: '/admin/help',
	requiredActions: [],
	superAdminOnly: false,
	observerAdminOnly: false,
	isGeneral: true,
	sortOrder: 10_000 + i,
}));

/** Everything the editor's "Sync modules from menu" button posts: the menu, then the general modules. */
export const buildSyncModules = (menu: readonly IMenuItem[]): IHelpModuleSync[] => [
	...buildHelpModules(menu),
	...GENERAL_HELP_MODULES,
];

/**
 * The module whose guides belong to a page. Longest matching route wins, so
 * `/admin/elections/manage-agents/live-map` is Manage Agents, not Elections. A `url: ''` sub shares
 * its parent's path; the parent (listed first) wins that tie, which is the module people mean.
 */
export const moduleKeyForPath = (pathname: string, modules: readonly IHelpModuleSync[]): string | null => {
	let best: IHelpModuleSync | null = null;
	modules.forEach((m) => {
		if (!isPathActive(m.routeUrl, pathname)) return;
		if (best === null || m.routeUrl.length > best.routeUrl.length) best = m;
	});
	return (best as IHelpModuleSync | null)?.key ?? null;
};

// ── Journey groups ────────────────────────────────────────────

export type JourneyId =
	| 'getting-started'
	| 'set-up'
	| 'recruit'
	| 'results'
	| 'analyse'
	| 'situation-room'
	| 'reach-voters'
	| 'wallet'
	| 'administration';

/** Home-page order. Grouped by what the reader is trying to do, not by where the menu files it. */
export const JOURNEYS: { id: JourneyId; title: string; blurb: string }[] = [
	{ id: 'getting-started', title: 'Getting started', blurb: 'Signing in, choosing an election and finding your way around.' },
	{ id: 'set-up', title: 'Set up an election', blurb: 'Parameters, attributes, the ballot, parties and the electoral map.' },
	{ id: 'recruit', title: 'Recruit & manage agents', blurb: 'Links, invitations, approvals, assignments and support.' },
	{ id: 'results', title: 'Collect & verify results', blurb: 'From an agent’s upload to a verified, audited result.' },
	{ id: 'analyse', title: 'Analyse', blurb: 'Live tallies, strongholds, analytics and the time machine.' },
	{ id: 'situation-room', title: 'Situation Room', blurb: 'Incidents, election pulse and the command room.' },
	{ id: 'reach-voters', title: 'Reach voters', blurb: 'Messaging, contacts, media and templates.' },
	{ id: 'wallet', title: 'Wallet & credits', blurb: 'Funding, rates and what each action costs.' },
	{ id: 'administration', title: 'Administration', blurb: 'Users, roles, keys and platform settings.' },
];

/**
 * Module key → journey. Looked up by LONGEST key prefix, so `elections.manage-agents` (a top-level
 * menu item that happens to live under `/admin/elections`) lands in Recruit, not Set up.
 */
const JOURNEY_BY_KEY: Record<string, JourneyId> = {
	'overview': 'getting-started',
	'observer-dashboard': 'getting-started',
	'my-election': 'getting-started',
	'general': 'getting-started',

	'election-parameters': 'set-up',
	'elections': 'set-up',
	'political-party': 'set-up',
	'election-observer': 'set-up',
	'electoral-divisions': 'set-up',
	'import-voters': 'set-up',
	'voters': 'set-up',
	'my-party': 'set-up',

	'elections.manage-agents': 'recruit',
	'election-observer.manage': 'recruit',
	'support': 'recruit',

	'result-verification': 'results',
	'result-audit': 'results',
	'election-results': 'results',
	'irev-extraction': 'results',
	'result-pipeline': 'results',
	'cancelled-units': 'results',
	'analyst-performance': 'results',

	'live-election': 'analyse',
	'strong-hold': 'analyse',
	'result-analytics': 'analyse',
	'result-time-machine': 'analyse',

	'vesr': 'situation-room',

	'voter-interactions': 'reach-voters',
	'contacts': 'reach-voters',
	'media-gallery': 'reach-voters',
	'message-templates': 'reach-voters',

	'my-wallet': 'wallet',
	'management.service-rates': 'wallet',
	'management.coupons': 'wallet',
	'management.wallets': 'wallet',

	'management': 'administration',
	'ai-result-extraction': 'administration',
};

/** Used only for a module this table has never heard of (a menu item added after this file). */
const JOURNEY_BY_SECTION: Record<string, JourneyId> = {
	'': 'getting-started',
	'My Campaign': 'analyse',
	'Operations': 'recruit',
	'Results': 'results',
	'Voter Outreach': 'reach-voters',
	'Finance': 'wallet',
	'Election Setup': 'set-up',
	'Directory': 'set-up',
	'Situation Room': 'situation-room',
	'System': 'administration',
};

export const journeyFor = (moduleKey: string, section = ''): JourneyId => {
	const parts = moduleKey.split('.');
	for (let n = parts.length; n > 0; n--) {
		const hit = JOURNEY_BY_KEY[parts.slice(0, n).join('.')];
		if (hit) return hit;
	}
	return JOURNEY_BY_SECTION[section] ?? 'getting-started';
};

export const isJourneyId = (v: unknown): v is JourneyId => typeof v === 'string' && JOURNEYS.some((j) => j.id === v);
